import type { TrafficRow } from '@netfoundry-ui/shared/model';

const EMPTY = '(empty)';

function newId(): string {
    if (typeof crypto !== 'undefined' && (crypto as any)?.randomUUID) {
        return (crypto as any).randomUUID();
    }
    return `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * The stable ziti id carried on a name bucket, or undefined when the template doesn't expose it yet
 * (today's v8 template keys on names only - see REPORTING_TEMPLATES_HANDOFF.md). Reads the shapes the
 * template is likely to add the id through, so this starts returning ids automatically once it does:
 * a `top_hits` sub-agg (named after the field, or `top`) with the id in `_source`, a nested `terms`
 * bucket on the id, or the id surfaced directly on the bucket. Confirm the exact path when the template
 * ships and trim this to it. Returns undefined for a blank/"(empty)" value so resolution falls back to
 * the stored name rather than keying on a non-id.
 */
function bucketId(bucket: any, field: string): string | undefined {
    const candidate =
        bucket?.[field]?.hits?.hits?.[0]?._source?.[field] ??
        bucket?.top?.hits?.hits?.[0]?._source?.[field] ??
        bucket?.[field]?.buckets?.[0]?.key ??
        (typeof bucket?.[field] === 'string' ? bucket?.[field] : undefined);
    const id = typeof candidate === 'string' ? candidate.trim() : '';
    return id && id !== EMPTY ? id : undefined;
}

/**
 * Flatten the `ncfabriccircuits` search response into one flow per observed
 * initiator -> identity -> service -> host -> terminator address:port bucket.
 * `circuits` is the bucket's circuit count (the template carries no byte volume).
 *
 * Pure and framework-agnostic so both the Traffic Analysis page and the
 * microsegment create page can parse the same response shape from one source.
 */
export function parseTrafficFlows(data: any): TrafficRow[] {
    const flows: TrafficRow[] = [];
    const initiatorBuckets = data?.aggregations?.initiator?.buckets ?? [];
    initiatorBuckets.forEach((initiator: any) => {
        // Anticipates the reporting template keying the initiator as "ip:port" (like the terminator) once
        // source port is exposed. Today the key is the IP only, so the port is empty - the split is safe
        // either way. If the template instead nests a port bucket, read it here. (TRAFFIC_USAGE_HANDOFF.md)
        const initiatorKey: string = initiator?.key || '';
        const portSep = initiatorKey.lastIndexOf(':');
        const initiatorIp = (portSep >= 0 ? initiatorKey.slice(0, portSep) : initiatorKey) || EMPTY;
        const initiatorPort = portSep >= 0 ? initiatorKey.slice(portSep + 1) : '';
        (initiator?.ingress_entity?.buckets ?? []).forEach((identity: any) => {
            (identity?.service_name?.buckets ?? []).forEach((service: any) => {
                (service?.hosting_entity?.buckets ?? []).forEach((host: any) => {
                    (host?.terminator_remote?.buckets ?? []).forEach((terminator: any) => {
                        const key: string = terminator?.key ?? '';
                        const [address, port] = key.split(':');
                        flows.push({
                            initiator_remote_ip: initiatorIp,
                            initiator_remote_port: initiatorPort,
                            identity_name: identity?.key,
                            host_entity_name: host?.key,
                            terminator_remote_address: address,
                            terminator_remote_port: port,
                            service_name: service?.key,
                            circuits: terminator?.circuits?.value ?? terminator?.doc_count ?? 0,
                            id: newId(),
                            // Stable ids when the template exposes them; undefined today (name-only).
                            identity_id: bucketId(identity, 'identity_id'),
                            host_id: bucketId(host, 'host_id'),
                            service_id: bucketId(service, 'service_id'),
                        });
                    });
                });
            });
        });
    });
    return flows;
}
